import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';

import { TurnoverConfigService } from '@app/services/turnover-config.service';
import { loadEnumerations } from '@shared/actions/enumerations.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  addFinish,
  addFinishFailure,
  addFinishSuccess,
  deleteFinish,
  deleteFinishFailure,
  deleteFinishSuccess,
  getElapsedDaysThresholds,
  getElapsedDaysThresholdsFailure,
  getElapsedDaysThresholdsSuccess,
  getTurnoverConfigurationByWorkflowStep,
  getTurnoverConfigurationByWorkflowStepFailure,
  getTurnoverConfigurationByWorkflowStepSuccess,
  getTurnoverConfigurationList,
  getTurnoverConfigurationListFailure,
  getTurnoverConfigurationListSuccess,
  loadFinish,
  loadFinishFailed,
  loadFinishSucceded,
  updateElapsedDaysThresholds,
  updateElapsedDaysThresholdsFailure,
  updateElapsedDaysThresholdsSuccess,
  updateFinish,
  updateFinishFailure,
  updateFinishSuccess,
  updateTurnoverFieldConfiguration,
  updateTurnoverFieldConfigurationFailure,
  updateTurnoverFieldConfigurationSuccess,
} from '../actions/turnover-configuration.actions';

@Injectable()
export class TurnoverConfigurationEffects {
  getTurnoverConfigurationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTurnoverConfigurationList),
      exhaustMap(action =>
        this.turnoverConfigService.getTurnoverConfigList().pipe(
          map(turnoverConfig => getTurnoverConfigurationListSuccess({ turnoverConfig })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverConfigurationList);
            return of(getTurnoverConfigurationListFailure());
          })
        )
      )
    )
  );

  getTurnoverConfigListByWorkflowStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTurnoverConfigurationByWorkflowStep),
      switchMap(action =>
        this.turnoverConfigService.getTurnoverConfigListByWorkflowType(action.workflowStep).pipe(
          map(turnoverConfig => getTurnoverConfigurationByWorkflowStepSuccess({ turnoverConfig })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverConfigurationElement);
            return of(getTurnoverConfigurationByWorkflowStepFailure());
          })
        )
      )
    )
  );

  updateTurnoverFieldConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTurnoverFieldConfiguration),
      mergeMap(action =>
        this.turnoverConfigService.updateTurnoverFieldConfiguration(action.turnoverConfig).pipe(
          map(() => updateTurnoverFieldConfigurationSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingTurnoverFieldConfiguration);
            return of(updateTurnoverFieldConfigurationFailure());
          })
        )
      )
    )
  );

  getElapsedDaysThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getElapsedDaysThresholds),
      switchMap(action =>
        this.turnoverConfigService.getElapsedDaysThresholds().pipe(
          map(thresholdsConfiguration => getElapsedDaysThresholdsSuccess({ thresholdsConfiguration })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRetrievingElapsedDaysThresholds);
            return of(getElapsedDaysThresholdsFailure());
          })
        )
      )
    )
  );

  updateElapsedDaysThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateElapsedDaysThresholds),
      mergeMap(action =>
        this.turnoverConfigService.updateElapsedDaysThresholds(action.thresholdsConfiguration).pipe(
          map(() => updateElapsedDaysThresholdsSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingElapsedDaysThresholds);
            return of(updateElapsedDaysThresholdsFailure());
          })
        )
      )
    )
  );

  loadFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFinish),
      mergeMap(action => {
        return this.turnoverConfigService.getFinishTypes().pipe(
          map(finishTypes => loadFinishSucceded({ finishTypes })),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorLoadingFinish);
            return of(loadFinishFailed());
          })
        );
      })
    )
  );

  addFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFinish),
      mergeMap(action => {
        return this.turnoverConfigService.addFinishType({ name: action.finish }).pipe(
          mergeMap(finishTypes => [addFinishSuccess(), loadFinish(), loadEnumerations()]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorAddingFinish);
            return of(addFinishFailure());
          })
        );
      })
    )
  );

  updateFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFinish),
      mergeMap(action => {
        return this.turnoverConfigService.updateFinishType({ id: action.id, name: action.name }).pipe(
          mergeMap(finishTypes => [updateFinishSuccess(), loadFinish(), loadEnumerations()]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingFinish);
            return of(updateFinishFailure());
          })
        );
      })
    )
  );

  deleteFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFinish),
      mergeMap(action => {
        return this.turnoverConfigService.deleteFinishType(action.id).pipe(
          mergeMap(finishTypes => [deleteFinishSuccess(), loadFinish(), loadEnumerations()]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorDeletingFinish);
            return of(deleteFinishFailure());
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private turnoverConfigService: TurnoverConfigService,
    private snackbarService: SnackbarService
  ) {}
}
